import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { Search, Sparkles, Loader2, Wand2 } from "lucide-react";

const PRESETS = [
  "Smells Like Teen Spirit — Nirvana",
  "Master of Puppets — Metallica",
  "In the Air Tonight — Phil Collins",
  "Rosanna — Toto",
  "Seven Nation Army — The White Stripes",
];

const DIFFICULTIES = ["Beginner", "Intermediate", "Pro", "God Mode"];

export default function TabGenerator({ onGenerate, loading, heroImage, initialQuery }) {
  const [query, setQuery] = useState(initialQuery || "");
  const [difficulty, setDifficulty] = useState("Intermediate");
  const [modifier, setModifier] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/suggestions", { params: { q: query } });
        setSuggestions(data);
      } catch (e) {
        /* ignore */
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  const submit = (q) => {
    const value = (q ?? query).trim();
    if (!value) return;
    onGenerate({ query: value, difficulty, modifier });
    setShowSuggest(false);
  };

  return (
    <section className="relative overflow-hidden radial-fade studio-grain">
      {heroImage && (
        <div className="absolute inset-0">
          <img src={heroImage} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0c10]/70 via-[#0b0c10]/85 to-[#0b0c10]" />
        </div>
      )}

      <div className="relative max-w-4xl mx-auto px-4 sm:px-8 pt-16 pb-14 sm:pt-24 sm:pb-20 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00f0ff]/30 bg-[#00f0ff]/5 text-xs font-tab tracking-widest uppercase text-[#00f0ff] mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          Powered by Claude
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95]">
          Any song.<br />
          <span className="cyan-text">Instant drum tabs.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
          Search a track and our AI transcribes a playable groove — then hear it
          with the built-in studio player.
        </p>

        <div ref={boxRef} className="relative mt-10 max-w-2xl mx-auto text-left">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Search a song or artist…"
              data-testid="song-search-input"
              className="h-14 pl-12 pr-4 text-base bg-[#12161f] border-white/10 focus-visible:ring-[#00f0ff]/50 rounded-xl"
            />
          </div>

          {showSuggest && suggestions.length > 0 && (
            <div className="absolute z-20 mt-2 w-full rounded-xl bg-[#12161f] border border-white/10 shadow-2xl overflow-hidden">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { const v = `${s.title} — ${s.artist}`; setQuery(v); submit(v); }}
                  className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-center gap-3 border-b border-white/5 last:border-0"
                  data-testid={`suggestion-${i}`}
                >
                  <Search className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">
                    <span className="text-slate-100">{s.title}</span>
                    <span className="text-slate-500"> — {s.artist}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger
                className="h-12 sm:w-48 bg-[#12161f] border-white/10"
                data-testid="difficulty-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#12161f] border-white/10">
                {DIFFICULTIES.map((d) => (
                  <SelectItem key={d} value={d} data-testid={`difficulty-${d}`}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1">
              <Wand2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={modifier}
                onChange={(e) => setModifier(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="Optional: 'add double-bass fills'"
                data-testid="modifier-input"
                className="h-12 pl-9 bg-[#12161f] border-white/10 text-sm"
              />
            </div>
          </div>

          <Button
            onClick={() => submit()}
            disabled={loading || !query.trim()}
            data-testid="generate-tab-button"
            className="w-full h-14 mt-3 bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-bold text-base uppercase tracking-wide glow-cyan rounded-xl"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Transcribing groove…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Generate Drum Tab
              </span>
            )}
          </Button>

          <div className="flex flex-wrap gap-2 mt-6 justify-center">
            {PRESETS.map((p, i) => (
              <button
                key={i}
                onClick={() => { setQuery(p); submit(p); }}
                disabled={loading}
                data-testid={`preset-song-chip-${i}`}
                className="px-3 py-1.5 rounded-full text-xs font-tab bg-white/5 border border-white/10 text-slate-300 hover:border-[#00f0ff]/40 hover:text-[#00f0ff] transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
